import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, KeyRound, Activity, Coins, CreditCard, BookOpen, Play, Copy, Check,
  Plus, RotateCw, Ban, Trash2, ArrowLeft, Sparkles, ShieldCheck, Webhook, Upload, X, Zap, Users, Maximize2, Send,
} from "lucide-react";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const CY = "#00F0FF";
const card = "rounded-xl border border-white/10 bg-[#141418] p-5";
const btn = "inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-[13px] font-semibold transition-colors";
const btnPrimary = `${btn} bg-[#00F0FF] text-black hover:bg-[#5df6ff]`;
const btnGhost = `${btn} border border-white/15 text-white/80 hover:bg-white/5`;
const inputCls = "w-full rounded-lg border border-white/15 bg-[#0D0D0E] px-3 py-2 text-[13px] text-white outline-none focus:border-[#00F0FF]/60";
const PERMS = ["image.generate", "image.edit", "models.read", "usage.read"];

const TABS = [
  { id: "overview", label: "Overview", icon: Sparkles },
  { id: "keys", label: "API Keys", icon: KeyRound },
  { id: "usage", label: "Usage", icon: Activity },
  { id: "credits", label: "Credits", icon: Coins },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
  { id: "teams", label: "Teams", icon: Users },
  { id: "playground", label: "Playground", icon: Play },
  { id: "docs", label: "Documentation", icon: BookOpen },
];

function CopyBtn({ text, testid }) {
  const [c, setC] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={() => { navigator.clipboard.writeText(text); setC(true); toast.success("Copied"); setTimeout(() => setC(false), 1500); }}
      className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-[11px] text-white/70 hover:bg-white/5">
      {c ? <Check size={12} /> : <Copy size={12} />}{c ? "Copied" : "Copy"}
    </button>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className={card}>
      <div className="text-[12px] uppercase tracking-wide text-white/40">{label}</div>
      <div className="mt-1 font-mono text-2xl font-bold text-white">{value}</div>
      {sub && <div className="mt-1 text-[12px] text-white/40">{sub}</div>}
    </div>
  );
}

export default function DeveloperPortal() {
  const navigate = useNavigate();
  const loc = useLocation();
  const [tab, setTab] = useState("overview");
  const [account, setAccount] = useState(null);
  const [credits, setCredits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  const origin = window.location.origin;

  const refresh = async () => {
    try {
      const [acc, cr, us, ks] = await Promise.all([
        api.cmAccount(), api.cmCredits(), api.cmUsage(), api.cmKeys(),
      ]);
      setAccount(acc); setCredits(cr); setUsage(us); setKeys(ks.keys || []);
    } catch (e) { toast.error("Failed to load developer data"); }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  // handle credit purchase return
  useEffect(() => {
    const p = new URLSearchParams(loc.search);
    const sid = p.get("credits_session");
    if (sid) {
      setTab("billing");
      api.cmCreditsStatus(sid).then((r) => {
        if (r.payment_status === "paid") { toast.success("Credits added to your account!"); refresh(); }
        else toast.message("Payment is processing…");
        window.history.replaceState(null, "", "/developer");
      }).catch(() => {});
    }
    if (p.get("credits_cancel")) { setTab("billing"); window.history.replaceState(null, "", "/developer"); }
  }, [loc.search]);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin" color={CY} /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="developer-portal">
      <div className="border-b border-white/10 bg-[#0D0D0E]">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/tools")} className="text-white/50 hover:text-white" data-testid="dev-back"><ArrowLeft size={18} /></button>
            <div>
              <div className="font-display text-lg font-bold">CaptionMotion AI — Developer Platform</div>
              <div className="text-[12px] text-white/40">Build with <span className="font-mono text-[#00F0FF]">captionmotion-ai-image-model</span></div>
            </div>
          </div>
          <div className="hidden items-center gap-3 sm:flex">
            {account?.unlimited
              ? <span className="rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-1 text-[12px] font-semibold" style={{ color: CY }}>Unlimited (Admin)</span>
              : <span className="rounded-full border border-white/15 px-3 py-1 text-[12px] text-white/70">{credits?.balance ?? 0} credits · {account?.plan?.toUpperCase()}</span>}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-5 py-6">
        <div className="mb-6 flex flex-wrap gap-1 rounded-xl border border-white/10 bg-[#0D0D0E] p-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dev-tab-${t.id}`}
              className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors ${tab === t.id ? "bg-[#1E1E24] text-white" : "text-white/50 hover:text-white/80"}`}
              style={tab === t.id ? { boxShadow: `inset 0 0 0 1px ${CY}55` } : {}}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview account={account} credits={credits} usage={usage} keys={keys} setTab={setTab} />}
        {tab === "keys" && <ApiKeys keys={keys} refresh={refresh} newKeysEnabled />}
        {tab === "usage" && <Usage usage={usage} />}
        {tab === "credits" && <Credits credits={credits} />}
        {tab === "billing" && <Billing credits={credits} origin={origin} />}
        {tab === "webhooks" && <Webhooks />}
        {tab === "teams" && <Teams />}
        {tab === "playground" && <Playground />}
        {tab === "docs" && <Docs origin={origin} />}
      </div>
    </div>
  );
}

function Overview({ account, credits, usage, keys, setTab }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Plan" value={(account?.plan || "free").toUpperCase()} sub={account?.unlimited ? "Admin bypass active" : "Rate-limited"} />
        <Stat label="Credit Balance" value={account?.unlimited ? "∞" : (credits?.balance ?? 0)} sub={`${credits?.reserved ?? 0} reserved`} />
        <Stat label="Requests (30d)" value={usage?.total ?? 0} sub={`${usage?.successful ?? 0} ok · ${usage?.failed ?? 0} failed`} />
        <Stat label="Active API Keys" value={keys.filter((k) => !k.revoked && !k.disabled).length} sub={`${keys.length} total`} />
      </div>
      <div className={card}>
        <div className="flex items-center gap-2 font-display text-base font-bold"><ShieldCheck size={16} color={CY} /> Get started in 3 steps</div>
        <ol className="mt-4 space-y-3 text-[13px] text-white/70">
          <li>1. Create an API key under <button className="text-[#00F0FF] underline" onClick={() => setTab("keys")}>API Keys</button> — you'll get a <span className="font-mono">cm_live_…</span> secret shown only once.</li>
          <li>2. Send a request to <span className="font-mono text-white">POST /v1/images/generate</span> with the model <span className="font-mono text-white">captionmotion-ai-image-model</span>.</li>
          <li>3. Track spend under <button className="text-[#00F0FF] underline" onClick={() => setTab("usage")}>Usage</button> &amp; top up under <button className="text-[#00F0FF] underline" onClick={() => setTab("billing")}>Billing</button>.</li>
        </ol>
      </div>
    </div>
  );
}

function ApiKeys({ keys, refresh }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", environment: "live", scopes: ["image.generate", "models.read", "usage.read"], expires_in_days: "", org_id: "" });
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orgs, setOrgs] = useState([]);
  useEffect(() => { api.cmOrgs().then((d) => setOrgs(d.orgs || [])).catch(() => {}); }, []);

  const toggleScope = (s) => setForm((f) => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s] }));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give the key a name");
    setBusy(true);
    try {
      const r = await api.cmKeyCreate({ name: form.name, environment: form.environment, scopes: form.scopes, expires_in_days: form.expires_in_days ? Number(form.expires_in_days) : null, org_id: form.org_id || null });
      setNewKey(r); setCreating(false); setForm({ name: "", environment: "live", scopes: ["image.generate", "models.read", "usage.read"], expires_in_days: "", org_id: "" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not create key"); }
    setBusy(false);
  };
  const act = async (fn, id, msg) => { try { await fn(id); toast.success(msg); refresh(); } catch { toast.error("Action failed"); } };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="font-display text-base font-bold">Your API Keys</div>
        <button className={btnPrimary} onClick={() => { setCreating(true); setNewKey(null); }} data-testid="dev-create-key-btn"><Plus size={15} /> Create Key</button>
      </div>

      {newKey && (
        <div className="rounded-xl border border-[#00F0FF]/40 bg-[#00F0FF]/5 p-5" data-testid="dev-new-key-panel">
          <div className="text-[13px] font-semibold text-[#00F0FF]">Copy this key now — it will never be shown again.</div>
          <div className="mt-3 flex items-center gap-3">
            <code className="flex-1 break-all rounded-lg bg-black/50 px-3 py-2 font-mono text-[13px] text-white" data-testid="dev-new-key-value">{newKey.key}</code>
            <CopyBtn text={newKey.key} testid="dev-new-key-copy" />
          </div>
          <button className="mt-3 text-[12px] text-white/50 underline" onClick={() => setNewKey(null)}>I've saved it — dismiss</button>
        </div>
      )}

      {creating && (
        <div className={card} data-testid="dev-create-key-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-[12px] text-white/50">Key name</label>
              <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Production server" data-testid="dev-key-name" />
            </div>
            <div>
              <label className="mb-1 block text-[12px] text-white/50">Environment</label>
              <select className={inputCls} value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} data-testid="dev-key-env">
                <option value="live">Live (cm_live_)</option>
                <option value="test">Test (cm_test_)</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] text-white/50">Expires in (days, optional)</label>
              <input className={inputCls} type="number" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })} placeholder="Never" data-testid="dev-key-expiry" />
            </div>
            {orgs.length > 0 && (
              <div>
                <label className="mb-1 block text-[12px] text-white/50">Bill to (optional)</label>
                <select className={inputCls} value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })} data-testid="dev-key-org">
                  <option value="">My personal credits</option>
                  {orgs.filter((o) => o.role !== "member").map((o) => <option key={o.id} value={o.id}>{`${o.name} (team)`}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="mb-1 block text-[12px] text-white/50">Permissions</label>
              <div className="flex flex-wrap gap-2">
                {PERMS.map((s) => (
                  <button key={s} onClick={() => toggleScope(s)} data-testid={`dev-scope-${s}`}
                    className={`rounded-md border px-2.5 py-1 text-[11px] font-mono ${form.scopes.includes(s) ? "border-[#00F0FF]/50 bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/50"}`}>{s}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button className={btnPrimary} onClick={create} disabled={busy} data-testid="dev-key-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Generate</button>
            <button className={btnGhost} onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-white/10">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-[#0D0D0E] text-[11px] uppercase text-white/40">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Key</th><th className="px-4 py-3">Scopes</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last used</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {keys.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No API keys yet.</td></tr>}
            {keys.map((k) => (
              <tr key={k.id} className="border-t border-white/5" data-testid={`dev-key-row-${k.id}`}>
                <td className="px-4 py-3 font-medium">{k.name}</td>
                <td className="px-4 py-3 font-mono text-white/60">{k.masked}</td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{k.scopes.map((s) => <span key={s} className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-mono text-white/50">{s}</span>)}</div></td>
                <td className="px-4 py-3">
                  {k.revoked ? <span className="text-red-400">Revoked</span> : k.disabled ? <span className="text-amber-400">Disabled</span> : <span style={{ color: CY }}>Active</span>}
                </td>
                <td className="px-4 py-3 text-white/40">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-3">
                  {!k.revoked && (
                    <div className="flex justify-end gap-1">
                      <button title="Rotate" className="rounded-md border border-white/15 p-1.5 text-white/60 hover:bg-white/5" onClick={() => act(async (id) => { const r = await api.cmKeyRotate(id); setNewKey(r); }, k.id, "Key rotated")} data-testid={`dev-key-rotate-${k.id}`}><RotateCw size={13} /></button>
                      <button title={k.disabled ? "Enable" : "Disable"} className="rounded-md border border-white/15 p-1.5 text-white/60 hover:bg-white/5" onClick={() => act((id) => api.cmKeyDisable(id, !k.disabled), k.id, k.disabled ? "Enabled" : "Disabled")} data-testid={`dev-key-disable-${k.id}`}><Ban size={13} /></button>
                      <button title="Revoke" className="rounded-md border border-red-500/30 p-1.5 text-red-400 hover:bg-red-500/10" onClick={() => act(api.cmKeyRevoke, k.id, "Key revoked")} data-testid={`dev-key-revoke-${k.id}`}><Trash2 size={13} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className={card}>
      <div className="mb-3 text-[12px] uppercase tracking-wide text-white/40">{title}</div>
      <div style={{ width: "100%", height: 200, minHeight: 200 }}>
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

function Usage({ usage }) {
  const [days, setDays] = useState(30);
  const [daily, setDaily] = useState(null);
  useEffect(() => { api.cmUsageDaily(days).then((d) => setDaily(d.series || [])).catch(() => setDaily([])); }, [days]);
  const short = (daily || []).map((r) => ({ ...r, d: r.date.slice(5) }));
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="grid flex-1 grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat label={`Total (${days}d)`} value={usage?.total ?? 0} />
          <Stat label="Successful" value={usage?.successful ?? 0} />
          <Stat label="Failed" value={usage?.failed ?? 0} />
          <Stat label="Avg latency" value={`${usage?.avg_latency_ms ?? 0}ms`} sub={`${usage?.credits_used ?? 0} credits used`} />
        </div>
      </div>
      <div className="flex gap-1" data-testid="dev-usage-range">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`rounded-md border px-3 py-1 text-[12px] ${days === d ? "border-[#00F0FF]/50 bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/50"}`} data-testid={`dev-range-${d}`}>{d}d</button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2" data-testid="dev-usage-charts">
        <ChartCard title="Generations per day">
          <BarChart data={short}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis dataKey="d" tick={{ fill: "#ffffff55", fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#ffffff55", fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#141418", border: "1px solid #ffffff20", fontSize: 12 }} />
            <Bar dataKey="successful" stackId="a" fill={CY} radius={[2, 2, 0, 0]} />
            <Bar dataKey="failed" stackId="a" fill="#ff5470" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ChartCard>
        <ChartCard title="Credits used per day">
          <AreaChart data={short}>
            <defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={CY} stopOpacity={0.5} /><stop offset="100%" stopColor={CY} stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis dataKey="d" tick={{ fill: "#ffffff55", fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#ffffff55", fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#141418", border: "1px solid #ffffff20", fontSize: 12 }} />
            <Area type="monotone" dataKey="credits" stroke={CY} fill="url(#cg)" strokeWidth={2} />
          </AreaChart>
        </ChartCard>
      </div>
      <div className="overflow-hidden rounded-xl border border-white/10">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-[#0D0D0E] text-[11px] uppercase text-white/40">
            <tr><th className="px-4 py-3">Request ID</th><th className="px-4 py-3">Endpoint</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Credits</th><th className="px-4 py-3">Latency</th><th className="px-4 py-3">When</th></tr>
          </thead>
          <tbody data-testid="dev-usage-table">
            {(usage?.recent || []).length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No requests yet.</td></tr>}
            {(usage?.recent || []).map((r) => (
              <tr key={r.id} className="border-t border-white/5">
                <td className="px-4 py-3 font-mono text-white/60">{r.id?.slice(0, 20)}</td>
                <td className="px-4 py-3 font-mono text-white/50">{r.endpoint || "—"}</td>
                <td className="px-4 py-3">{r.status === "completed" ? <span style={{ color: CY }}>ok</span> : <span className="text-red-400">{r.error_code || r.status || "failed"}</span>}</td>
                <td className="px-4 py-3">{r.credits_used ?? 0}</td>
                <td className="px-4 py-3 text-white/50">{r.latency_ms ? `${r.latency_ms}ms` : "—"}</td>
                <td className="px-4 py-3 text-white/40">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Webhooks() {
  const [data, setData] = useState(null);
  const [url, setUrl] = useState("");
  const [newSecret, setNewSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setData(await api.cmWebhooks()); } catch { toast.error("Failed to load webhooks"); } };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!url.startsWith("http")) return toast.error("Enter a valid https URL");
    setBusy(true);
    try { const r = await api.cmWebhookCreate(url); setNewSecret(r); setUrl(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Failed"); }
    setBusy(false);
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch { toast.error("Action failed"); } };
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="animate-spin" color={CY} /></div>;
  return (
    <div className="space-y-5">
      <div className={card}>
        <div className="font-display text-base font-bold">Add a webhook endpoint</div>
        <p className="mt-1 text-[12px] text-white/40">We POST a signed event (<span className="font-mono">X-CM-Signature: sha256=…</span>) on <span className="font-mono">image.completed</span> / <span className="font-mono">image.failed</span>, with retries.</p>
        <div className="mt-3 flex gap-2">
          <input className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-app.com/webhooks/captionmotion" data-testid="dev-webhook-url" />
          <button className={btnPrimary} onClick={create} disabled={busy} data-testid="dev-webhook-create">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add</button>
        </div>
      </div>
      {newSecret && (
        <div className="rounded-xl border border-[#00F0FF]/40 bg-[#00F0FF]/5 p-5" data-testid="dev-webhook-secret-panel">
          <div className="text-[13px] font-semibold text-[#00F0FF]">Signing secret — copy it now, shown once.</div>
          <div className="mt-3 flex items-center gap-3">
            <code className="flex-1 break-all rounded-lg bg-black/50 px-3 py-2 font-mono text-[13px] text-white">{newSecret.secret}</code>
            <CopyBtn text={newSecret.secret} testid="dev-webhook-secret-copy" />
          </div>
          <button className="mt-3 text-[12px] text-white/50 underline" onClick={() => setNewSecret(null)}>Dismiss</button>
        </div>
      )}
      <div className="overflow-hidden rounded-xl border border-white/10">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-[#0D0D0E] text-[11px] uppercase text-white/40"><tr><th className="px-4 py-3">URL</th><th className="px-4 py-3">Secret</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last delivery</th><th className="px-4 py-3"></th></tr></thead>
          <tbody data-testid="dev-webhooks-table">
            {data.webhooks.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">No webhooks yet.</td></tr>}
            {data.webhooks.map((w) => (
              <tr key={w.id} className="border-t border-white/5" data-testid={`dev-webhook-row-${w.id}`}>
                <td className="px-4 py-3 font-mono text-white/70">{w.url}</td>
                <td className="px-4 py-3 font-mono text-white/40">{w.secret_masked}</td>
                <td className="px-4 py-3">{w.enabled ? <span style={{ color: CY }}>enabled</span> : <span className="text-amber-400">disabled</span>}</td>
                <td className="px-4 py-3 text-white/40">{w.last_delivery ? new Date(w.last_delivery).toLocaleString() : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button title="Test" className="rounded-md border border-white/15 p-1.5 text-white/60 hover:bg-white/5" onClick={() => act(() => api.cmWebhookTest(w.id).then((r) => toast[r.delivered ? "success" : "error"](r.delivered ? `Delivered (${r.response_code})` : "Delivery failed")), "")} data-testid={`dev-webhook-test-${w.id}`}><Zap size={13} /></button>
                    <button title={w.enabled ? "Disable" : "Enable"} className="rounded-md border border-white/15 p-1.5 text-white/60 hover:bg-white/5" onClick={() => act(() => api.cmWebhookDisable(w.id, !w.enabled), w.enabled ? "Disabled" : "Enabled")} data-testid={`dev-webhook-disable-${w.id}`}><Ban size={13} /></button>
                    <button title="Delete" className="rounded-md border border-red-500/30 p-1.5 text-red-400 hover:bg-red-500/10" onClick={() => act(() => api.cmWebhookDelete(w.id), "Deleted")} data-testid={`dev-webhook-delete-${w.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={card}>
        <div className="mb-2 text-[12px] uppercase tracking-wide text-white/40">Recent deliveries</div>
        {data.deliveries.length === 0 ? <div className="text-[13px] text-white/40">No deliveries yet.</div> : (
          <div className="space-y-1 font-mono text-[12px]">
            {data.deliveries.slice(0, 8).map((d) => (
              <div key={d.id} className="flex items-center gap-3 text-white/60"><span className={d.status === "delivered" ? "text-[#00F0FF]" : "text-red-400"}>{d.status}</span><span>{d.event}</span><span className="text-white/30">{d.response_code}</span><span className="text-white/30">{d.created_at ? new Date(d.created_at).toLocaleTimeString() : ""}</span>
                <button onClick={() => act(() => api.cmWebhookResend(d.id).then((r) => toast[r.status === "delivered" ? "success" : "error"](`Resent: ${r.status} ${r.response_code || ""}`)), "")} className="inline-flex items-center gap-1 rounded border border-white/15 px-1.5 py-0.5 text-[10px] text-white/60 hover:bg-white/5" data-testid={`dev-webhook-resend-${d.id}`}><Send size={10} /> Resend</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Credits({ credits }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Balance" value={credits?.unlimited ? "∞" : (credits?.balance ?? 0)} />
        <Stat label="Reserved" value={credits?.reserved ?? 0} />
        <Stat label="Lifetime purchased" value={credits?.lifetime_purchased ?? 0} />
        <Stat label="Lifetime used" value={credits?.lifetime_used ?? 0} />
      </div>
      <div className={card}>
        <div className="text-[12px] uppercase tracking-wide text-white/40">Pricing</div>
        <div className="mt-2 flex flex-wrap gap-4 text-[13px]">
          {Object.entries(credits?.pricing || {}).map(([k, v]) => (
            <span key={k} className="font-mono text-white/70">{k}: <span style={{ color: CY }}>{v} credits</span></span>
          ))}
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-white/10">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-[#0D0D0E] text-[11px] uppercase text-white/40">
            <tr><th className="px-4 py-3">Type</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Balance after</th><th className="px-4 py-3">Note</th><th className="px-4 py-3">When</th></tr>
          </thead>
          <tbody data-testid="dev-credits-table">
            {(credits?.transactions || []).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">No transactions yet.</td></tr>}
            {(credits?.transactions || []).map((t) => (
              <tr key={t.id} className="border-t border-white/5">
                <td className="px-4 py-3 capitalize">{t.type}</td>
                <td className={`px-4 py-3 font-mono ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</td>
                <td className="px-4 py-3 font-mono text-white/60">{t.balance_after}</td>
                <td className="px-4 py-3 text-white/50">{t.note || "—"}</td>
                <td className="px-4 py-3 text-white/40">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Billing({ credits, origin }) {
  const [busy, setBusy] = useState(null);
  const buy = async (pid) => {
    setBusy(pid);
    try {
      const r = await api.cmBuyCredits(pid, origin);
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(null); }
  };
  return (
    <div className="space-y-5">
      <div className={card}>
        <div className="text-[12px] uppercase tracking-wide text-white/40">Current balance</div>
        <div className="mt-1 font-mono text-3xl font-bold" style={{ color: CY }}>{credits?.unlimited ? "∞ (Admin)" : `${credits?.balance ?? 0} credits`}</div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3" data-testid="dev-packages">
        {(credits?.packages || []).map((p) => (
          <div key={p.id} className={`${card} flex flex-col`} data-testid={`dev-package-${p.id}`}>
            <div className="font-display text-lg font-bold">{p.name}</div>
            <div className="mt-1 font-mono text-3xl font-bold" style={{ color: CY }}>{p.credits}</div>
            <div className="text-[12px] text-white/40">credits</div>
            <div className="mt-3 text-[13px] text-white/70">{p.display}</div>
            <button className={`${btnPrimary} mt-4 justify-center`} onClick={() => buy(p.id)} disabled={busy === p.id} data-testid={`dev-buy-${p.id}`}>
              {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Buy
            </button>
          </div>
        ))}
      </div>
      <p className="text-[12px] text-white/40">Payments are processed securely via Stripe. Purchased credits are added to your ledger and used for API &amp; website generations.</p>
    </div>
  );
}

function Teams() {
  const [orgs, setOrgs] = useState(null);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const load = async () => { try { const d = await api.cmOrgs(); setOrgs(d.orgs || []); if (!active && d.orgs?.[0]) setActive(d.orgs[0].id); } catch { toast.error("Failed to load"); } };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (active) api.cmOrgDetail(active).then(setDetail).catch(() => setDetail(null)); }, [active]);
  const create = async () => { if (!name.trim()) return; try { const o = await api.cmOrgCreate(name); setName(""); await load(); setActive(o.id); toast.success("Organization created"); } catch { toast.error("Failed"); } };
  const invite = async () => { if (!email.trim()) return; try { await api.cmOrgInvite(active, email, "member"); setEmail(""); api.cmOrgDetail(active).then(setDetail); toast.success("Member invited"); } catch (e) { toast.error(e?.response?.data?.detail?.message || "Failed"); } };
  const removeM = async (uid) => { try { await api.cmOrgRemoveMember(active, uid); api.cmOrgDetail(active).then(setDetail); toast.success("Removed"); } catch (e) { toast.error(e?.response?.data?.detail?.message || "Failed"); } };
  if (orgs === null) return <div className="flex justify-center py-12"><Loader2 className="animate-spin" color={CY} /></div>;
  return (
    <div className="space-y-5">
      <div className={`${card} flex flex-wrap items-end gap-3`}>
        <div className="flex-1">
          <label className="mb-1 block text-[12px] text-white/50">Create a team workspace</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Studio" data-testid="dev-org-name" />
        </div>
        <button className={btnPrimary} onClick={create} data-testid="dev-org-create"><Plus size={14} /> Create</button>
      </div>
      {orgs.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="dev-org-list">
          {orgs.map((o) => (
            <button key={o.id} onClick={() => setActive(o.id)} className={`rounded-lg border px-3 py-1.5 text-[13px] ${active === o.id ? "border-[#00F0FF]/50 bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/60"}`} data-testid={`dev-org-${o.id}`}>{o.name} <span className="text-[10px] opacity-60">{o.role}</span></button>
          ))}
        </div>
      )}
      {detail && (
        <div className="space-y-4" data-testid="dev-org-detail">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <Stat label="Shared credits" value={detail.credits.balance} sub={`${detail.credits.reserved} reserved`} />
            <Stat label="Members" value={detail.members.length} />
            <Stat label="Requests (30d)" value={detail.usage.total} />
          </div>
          <div className={card}>
            <div className="mb-3 flex items-center justify-between">
              <div className="font-display text-sm font-bold">Members</div>
              {detail.org.role !== "member" && (
                <div className="flex gap-2">
                  <input className={`${inputCls} w-56`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@email.com" data-testid="dev-org-invite-email" />
                  <button className={btnGhost} onClick={invite} data-testid="dev-org-invite"><Plus size={13} /> Invite</button>
                </div>
              )}
            </div>
            <div className="space-y-1">
              {detail.members.map((m) => (
                <div key={m.user_id} className="flex items-center justify-between border-b border-white/5 py-2 text-[13px]" data-testid={`dev-org-member-${m.user_id}`}>
                  <span className="text-white/80">{m.email} <span className="ml-2 rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-white/50">{m.role}</span></span>
                  {detail.org.role !== "member" && m.role !== "owner" && <button className="text-red-400 hover:underline" onClick={() => removeM(m.user_id)} data-testid={`dev-org-remove-${m.user_id}`}>Remove</button>}
                </div>
              ))}
            </div>
          </div>
          <div className={card}>
            <div className="mb-2 font-display text-sm font-bold">Organization API keys ({detail.keys.length})</div>
            <p className="text-[12px] text-white/40">Create org-scoped keys from the API Keys tab (they bill this team's shared credits). Owners/admins only.</p>
            {detail.keys.map((k) => <div key={k.id} className="mt-2 font-mono text-[12px] text-white/60">{k.masked} · {k.environment} · {k.disabled || k.revoked ? "inactive" : "active"}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

function Playground() {
  const [prompt, setPrompt] = useState("A cinematic product photo of a glowing sneaker on a black pedestal");
  const [ar, setAr] = useState("1:1");
  const [res, setRes] = useState(1024);
  const [refs, setRefs] = useState([]); // base64 (no prefix)
  const [asyncMode, setAsyncMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [lastId, setLastId] = useState(null);
  const [status, setStatus] = useState(null);
  const [upBusy, setUpBusy] = useState(false);

  const onFiles = async (files) => {
    const arr = Array.from(files).slice(0, 4);
    const read = (f) => new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(",")[1]); r.readAsDataURL(f); });
    const b64 = await Promise.all(arr.map(read));
    setRefs((cur) => [...cur, ...b64].slice(0, 4));
  };

  const poll = async (id) => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const r = await api.cmHistory();
        const job = (r.requests || []).find((x) => x.id === id);
        if (job) { setStatus(job.status); if (job.status === "completed") { setOut({ images: job.outputs || [] }); setLastId(id); return; } if (job.status === "failed") { toast.error("Generation failed"); return; } }
      } catch {}
    }
  };

  const run = async () => {
    setBusy(true); setOut(null); setStatus(null);
    try {
      const body = { model: "captionmotion-ai-image-model", prompt, aspect_ratio: ar, resolution: Number(res) };
      if (refs.length) body.reference_images = refs;
      if (asyncMode) body.async = true;
      const r = await api.cmGenerate(body);
      if (asyncMode && r.status === "queued") { setStatus("queued"); toast.message("Job queued…"); poll(r.id); }
      else { setOut(r); setLastId(r.id); setStatus("completed"); }
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Generation failed"); }
    setBusy(false);
  };
  const upscale = async () => {
    if (!lastId) return;
    setUpBusy(true);
    try { const r = await api.cmUpscale(lastId, 2); setOut(r); setLastId(r.id); toast.success("Upscaled 2×"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Upscale failed"); }
    setUpBusy(false);
  };
  const imgUrl = out?.images?.[0]?.url ? (out.images[0].url.startsWith("http") ? out.images[0].url : `${API}${out.images[0].url}`) : null;
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className={card}>
        <label className="mb-1 block text-[12px] text-white/50">Prompt</label>
        <textarea className={`${inputCls} h-24 resize-none`} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="dev-pg-prompt" />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] text-white/50">Aspect ratio</label>
            <select className={inputCls} value={ar} onChange={(e) => setAr(e.target.value)} data-testid="dev-pg-ar">
              {["1:1", "4:5", "9:16", "16:9", "3:4", "4:3"].map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[12px] text-white/50">Resolution</label>
            <select className={inputCls} value={res} onChange={(e) => setRes(e.target.value)} data-testid="dev-pg-res">
              {[512, 768, 1024, 1536].map((r) => <option key={r} value={r}>{`${r}px`}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="mb-1 block text-[12px] text-white/50">Reference images (optional, up to 4)</label>
          <div className="flex flex-wrap items-center gap-2">
            {refs.map((b, i) => (
              <div key={i} className="relative">
                <img src={`data:image/png;base64,${b}`} alt="ref" className="h-14 w-14 rounded-md border border-white/10 object-cover" />
                <button onClick={() => setRefs((c) => c.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-500 p-0.5 text-white" data-testid={`dev-pg-ref-remove-${i}`}><X size={11} /></button>
              </div>
            ))}
            {refs.length < 4 && (
              <label className="flex h-14 w-14 cursor-pointer items-center justify-center rounded-md border border-dashed border-white/20 text-white/40 hover:border-[#00F0FF]/50" data-testid="dev-pg-ref-upload">
                <Upload size={16} /><input type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
              </label>
            )}
          </div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-[13px] text-white/70">
          <input type="checkbox" checked={asyncMode} onChange={(e) => setAsyncMode(e.target.checked)} data-testid="dev-pg-async" /> Async mode (queue &amp; poll)
        </label>
        <button className={`${btnPrimary} mt-4`} onClick={run} disabled={busy} data-testid="dev-pg-run">{busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Generate</button>
        <p className="mt-3 text-[11px] text-white/40">Runs against <span className="font-mono">captionmotion-ai-image-model</span>. Credits are deducted from your balance.</p>
      </div>
      <div className={`${card} flex flex-col items-center justify-center`} data-testid="dev-pg-output">
        {busy || (status && status !== "completed" && status !== "failed") ? (
          <div className="text-center"><Loader2 className="mx-auto animate-spin" color={CY} />{status && <div className="mt-2 text-[12px] uppercase text-white/40">{status}</div>}</div>
        ) : imgUrl ? (
          <>
            <img src={imgUrl} alt="generated" className="max-h-[340px] rounded-lg" data-testid="dev-pg-image" />
            <button className={`${btnGhost} mt-3`} onClick={upscale} disabled={upBusy} data-testid="dev-pg-upscale">{upBusy ? <Loader2 size={13} className="animate-spin" /> : <Maximize2 size={13} />} Upscale 2×</button>
          </>
        ) : <div className="text-white/30">Your generated image appears here</div>}
      </div>
    </div>
  );
}

function CodeBlock({ children, testid }) {
  return (
    <div className="relative rounded-lg border border-white/10 bg-black/50">
      <div className="absolute right-2 top-2"><CopyBtn text={children} testid={testid} /></div>
      <pre className="overflow-x-auto p-4 pr-16 font-mono text-[12px] leading-relaxed text-white/80"><code>{children}</code></pre>
    </div>
  );
}

function Docs({ origin }) {
  const base = `${origin}/api/v1`;
  return (
    <div className="max-w-3xl space-y-6 text-[13px] text-white/70">
      <a href="/docs" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-1.5 text-[12px] font-semibold text-[#00F0FF]" data-testid="dev-open-full-docs"><BookOpen size={13} /> Open full public API docs →</a>
      <section>
        <h3 className="font-display text-base font-bold text-white">Quick start</h3>
        <p className="mt-2">Create an API key, then call the CaptionMotion image API. All requests authenticate with a Bearer key and use the model <span className="font-mono text-[#00F0FF]">captionmotion-ai-image-model</span>. You never touch the underlying provider.</p>
      </section>
      <section>
        <h3 className="font-display text-base font-bold text-white">Authentication</h3>
        <p className="mt-2">Pass your key in the <span className="font-mono">Authorization</span> header:</p>
        <CodeBlock testid="dev-doc-auth">{`Authorization: Bearer cm_live_xxxxxxxxxxxx`}</CodeBlock>
      </section>
      <section>
        <h3 className="font-display text-base font-bold text-white">Generate an image</h3>
        <CodeBlock testid="dev-doc-generate">{`curl -X POST "${base}/images/generate" \\
  -H "Authorization: Bearer cm_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -d '{
    "model": "captionmotion-ai-image-model",
    "prompt": "A premium product portrait, studio lighting",
    "aspect_ratio": "4:5",
    "resolution": 1024
  }'`}</CodeBlock>
        <p className="mt-2">Response:</p>
        <CodeBlock testid="dev-doc-response">{`{
  "id": "imgreq_...",
  "model": "captionmotion-ai-image-model",
  "status": "completed",
  "images": [{ "id": "img_...", "url": "https://...", "width": 819, "height": 1024 }],
  "usage": { "credits_used": 10 }
}`}</CodeBlock>
      </section>
      <section>
        <h3 className="font-display text-base font-bold text-white">Other endpoints</h3>
        <ul className="mt-2 space-y-1 font-mono text-[12px]">
          <li><span className="text-[#00F0FF]">POST</span> {base}/images/edit</li>
          <li><span className="text-[#00F0FF]">GET</span> {base}/images/{`{id}`}</li>
          <li><span className="text-[#00F0FF]">GET</span> {base}/models</li>
          <li><span className="text-[#00F0FF]">GET</span> {base}/usage</li>
          <li><span className="text-[#00F0FF]">GET</span> {base}/account</li>
        </ul>
      </section>
      <section>
        <h3 className="font-display text-base font-bold text-white">Errors</h3>
        <p className="mt-2">Errors are normalized: <span className="font-mono">401 INVALID_API_KEY</span>, <span className="font-mono">402 INSUFFICIENT_CREDITS</span>, <span className="font-mono">403 INSUFFICIENT_PERMISSION</span>, <span className="font-mono">404 MODEL_NOT_FOUND</span>, <span className="font-mono">429 RATE_LIMITED</span>. Retry <span className="font-mono">429</span>/5xx with backoff and reuse your <span className="font-mono">Idempotency-Key</span>.</p>
      </section>
      <section>
        <h3 className="font-display text-base font-bold text-white">Python</h3>
        <CodeBlock testid="dev-doc-python">{`import requests
r = requests.post("${base}/images/generate",
  headers={"Authorization": "Bearer cm_live_xxxx"},
  json={"model": "captionmotion-ai-image-model",
        "prompt": "A neon cyberpunk city street", "aspect_ratio": "16:9"})
print(r.json()["images"][0]["url"])`}</CodeBlock>
      </section>
    </div>
  );
}
